import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FileText, History, Database } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const evidenceCards = [
  {
    id: 'A',
    label: 'Lane A — Primary text',
    quote: '…the minimum wage shall be $7.25 per hour…',
    source: 'Fair Labor Standards Act',
    date: '2009-07-24',
    icon: FileText,
    rotation: -6,
    zIndex: 1,
    left: '18vw',
    top: '40vh',
  },
  {
    id: 'B',
    label: 'Lane B — Record',
    quote: '…the purpose of the minimum wage is to protect the lowest-paid workers…',
    source: 'Congressional Record',
    date: '1966-09-14',
    icon: History,
    rotation: 0,
    zIndex: 2,
    left: '33vw',
    top: '44vh',
  },
  {
    id: 'C',
    label: 'Lane C — Data',
    quote: '…in 2023, 1.6 million workers earned wages at or below the federal minimum…',
    source: 'Bureau of Labor Statistics',
    date: '2024-03-01',
    icon: Database,
    rotation: 6,
    zIndex: 3,
    left: '48vw',
    top: '40vh',
  },
];

export function EvidencePackSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const legendRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // ENTRANCE (0% - 30%)
      scrollTl.fromTo(
        headlineRef.current,
        { y: '-12vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0
      );

      // Card A - from left
      scrollTl.fromTo(
        cardRefs.current[0],
        { x: '-50vw', rotation: -14, opacity: 0 },
        { x: 0, rotation: -6, opacity: 1, ease: 'none' },
        0
      );

      // Card B - from bottom
      scrollTl.fromTo(
        cardRefs.current[1],
        { y: '60vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.05
      );

      // Card C - from right
      scrollTl.fromTo(
        cardRefs.current[2],
        { x: '50vw', rotation: 14, opacity: 0 },
        { x: 0, rotation: 6, opacity: 1, ease: 'none' },
        0.1
      );

      scrollTl.fromTo(
        legendRef.current,
        { y: '4vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.2
      );

      // SETTLE (30% - 70%) - hold position

      // EXIT (70% - 100%)
      scrollTl.fromTo(
        cardRefs.current,
        { y: 0, opacity: 1 },
        { y: '-26vh', opacity: 0, ease: 'power2.in', stagger: 0.02 },
        0.7
      );

      scrollTl.fromTo(headlineRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.75);

      scrollTl.fromTo(legendRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.78);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pinned z-20 flex items-center justify-center bg-[#F6F5F2]"
    >
      {/* Headline Group */}
      <div
        ref={headlineRef}
        className="absolute"
        style={{ left: '9vw', top: '10vh', width: '46vw' }}
      >
        <h2
          className="font-display font-bold leading-[1.05] tracking-[-0.02em] text-[#0B0D10]"
          style={{ fontSize: 'clamp(34px, 3.6vw, 52px)' }}
        >
          One search. Three lanes.
        </h2>
        <p
          className="mt-4 leading-relaxed text-[#6B6F78]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)', width: '40vw' }}
        >
          Primary text. Contemporaneous record. Modern official data. All dated, linked, and ready
          to cite.
        </p>
      </div>

      {/* Evidence Cards */}
      {evidenceCards.map((card, index) => {
        const Icon = card.icon;
        return (
          <div
            key={card.id}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
            className="receipt-card card-float absolute p-5"
            style={{
              left: card.left,
              top: card.top,
              width: '34vw',
              transform: `rotate(${card.rotation}deg)`,
              zIndex: card.zIndex,
              animationDelay: `${index * 0.5}s`,
            }}
          >
            {/* Lane Label */}
            <div className="mb-4 flex items-center gap-2">
              <Icon className="h-4 w-4 text-[#E1062C]" />
              <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                {card.label}
              </span>
            </div>

            {/* Quote */}
            <p
              className="mb-4 font-medium leading-relaxed text-[#0B0D10]"
              style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }}
            >
              {card.quote}
            </p>

            {/* Source & Date */}
            <div className="flex items-center justify-between border-t border-[rgba(11,13,16,0.08)] pt-4">
              <span className="text-sm text-[#6B6F78]">{card.source}</span>
              <span className="font-mono text-xs text-[#E1062C]">{card.date}</span>
            </div>
          </div>
        );
      })}

      {/* Source Legend */}
      <div ref={legendRef} className="absolute" style={{ left: '9vw', top: '78vh' }}>
        <p className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
          Sources: Federal Register • Congress.gov • FRED
        </p>
      </div>
    </section>
  );
}
